import { MailCodeResponse } from '@thesavingsgroup/apex-api/dto/MailCodeResponse';
import { AuthType } from '@thesavingsgroup/auth/AuthType';
import { isNonEmptyString } from '@thesavingsgroup/utils/isNonEmptyString';
import i18n from 'i18next';
import { capitalize, get as lodashGet, isEmpty, toString } from 'lodash';
import { StateCreator } from 'zustand';

import { inputEventHandler } from '../../utils/component/inputEventHandler';
import { DeliverPinLocation } from '../pinStore/enums/DeliverPinLocation';
import { DeliverPinParams } from '../pinStore/types/DeliverPinParams';
import { UserExistsExceptionCode } from '../userStore/enums/UserExistsExceptionCode';
import { MailCodeState } from './types/MailCodeState';
import { MailCodeStateDependencies } from './types/MailCodeStateDependencies';
import { SetMailCodeActivationStateParams } from './types/SetMailCodeActivationStateParams';

export const keysToPersist = [
  'firstName',
  'lastName',
  'mailCode',
  'mailCodeData',
  'pinPurpose',
  'primaryEmailAddress',
  'primaryPhoneNumber',
  'mailCodeUniqueIdentifierData',
];

export const mailCodeStore =
  ({
    useAnalyticStore,
    useApiStore,
    useParameterStore,
  }: MailCodeStateDependencies): StateCreator<MailCodeState> =>
  (set, get) => ({
    fetchMailCodeDataFailedMessage: null,
    fetchMailCodeDataSuccess: false,
    firstName: '',
    isMailCodeInvalidSourceError: false,
    isMailCodeNotFoundError: false,
    lastName: '',
    mailCode: null,
    mailCodeData: null,
    mailCodeUniqueIdentifierData: {},
    pinPurpose: null,
    primaryEmailAddress: '',
    primaryPhoneNumber: '',

    computed: {
      get applicantFirstName(): string | null {
        const { mailCodeData } = get();

        if (!mailCodeData) {
          return null;
        }

        return capitalize(mailCodeData.payload.applicant.firstName);
      },
      get applicantLastName(): string | null {
        const { mailCodeData } = get();

        if (!mailCodeData) {
          return null;
        }

        return capitalize(mailCodeData.payload.applicant.lastName);
      },
      get isCreateUserPinPurpose(): boolean {
        const { pinPurpose } = get();

        return pinPurpose === AuthType.ACCOUNT_REGISTRATION_USING_PIN;
      },
      get isEditPhonePinPurpose(): boolean {
        const { pinPurpose } = get();

        return pinPurpose === AuthType.EDIT_PHONE_USING_PIN;
      },
      get isPinPurposeEmpty(): boolean {
        const { pinPurpose } = get();

        return isEmpty(pinPurpose);
      },
      get isPrimaryPhoneNumberEmpty(): boolean {
        const { primaryPhoneNumber } = get();

        return isEmpty(primaryPhoneNumber);
      },
      get originalPaymentAmount(): string {
        const { mailCodeData } = get();

        if (!mailCodeData) {
          return '';
        }

        return inputEventHandler.number.formatter(
          mailCodeData.payload.mailerCode.currentPayment,
        );
      },
      get preApprovalAmount(): string {
        const { mailCodeData } = get();

        if (!mailCodeData) {
          return '';
        }

        return inputEventHandler.number.formatter(
          mailCodeData.payload.mailerCode.preApprovalAmount,
        );
      },
      get refinancePaymentAmount(): string {
        const { mailCodeData } = get();

        if (!mailCodeData) {
          return '';
        }

        return inputEventHandler.number.formatter(
          mailCodeData.payload.mailerCode.newPayment,
        );
      },
      get savingsAmount(): string {
        const { mailCodeData } = get();

        if (!mailCodeData) {
          return '';
        }

        return inputEventHandler.number.formatter(
          mailCodeData.payload.mailerCode.potentialSavings,
        );
      },
      get shouldFetchMailCodeDataOnLoad(): boolean {
        const { fetchMailCodeDataFailedMessage } = get();
        const { mailCode: parameterMailCode } = useParameterStore.getState();

        return (
          // mail code is present in the URL parameters
          isNonEmptyString(parameterMailCode) &&
          // we have not already attempted to fetch mail code data in which it failed
          isEmpty(fetchMailCodeDataFailedMessage)
        );
      },
      get vehiclePayoffAmount(): string | null {
        const { mailCodeData } = get();

        if (!mailCodeData) {
          return null;
        }

        return String(mailCodeData.payload.vehicle.payoffAmount);
      },
    },
    determinePinPurpose: (
      userExistsException: UserExistsExceptionCode,
    ): AuthType => {
      /*
        The mail code experience can lead to three different pin purposes:
        1. ACCOUNT_REGISTRATION_USING_PIN - we should create a new user account
        2. EDIT_PHONE_USING_PIN - we should update the phone number on an existing account
        3. ACCOUNT_SIGN_IN_USING_PIN - we should sign in to an existing account

        For more information, see: https://savingsgroup.atlassian.net/wiki/x/AQBvRQ
      */

      const shouldCreateUser =
        userExistsException === UserExistsExceptionCode.BOTH_NOT_FOUND;

      const shouldUpdatePhoneNumber =
        userExistsException ===
        UserExistsExceptionCode.EMAIL_ADDRESS_REGISTERED;

      if (shouldCreateUser) {
        return AuthType.ACCOUNT_REGISTRATION_USING_PIN;
      }

      if (shouldUpdatePhoneNumber) {
        return AuthType.EDIT_PHONE_USING_PIN;
      }

      return AuthType.ACCOUNT_SIGN_IN_USING_PIN;
    },
    fetchMailCodeData: (mailCode: string): Promise<void> => {
      const { leadChannelCode } = useParameterStore.getState();
      const { sendMailCodeErrorAnalytics, sendMailCodeFoundAnalytics } =
        useAnalyticStore.getState();

      const portalHttpRequest = useApiStore.getState().portalApi();

      set({
        fetchMailCodeDataFailedMessage: null,
        fetchMailCodeDataSuccess: false,
        isMailCodeInvalidSourceError: false,
        isMailCodeNotFoundError: false,
      });

      return portalHttpRequest
        .get<never, { data: MailCodeResponse }>(
          `/2.0/mail-code?leadChannelCode=${leadChannelCode}&mailCode=${mailCode}`,
        )
        .then(({ data }) => {
          sendMailCodeFoundAnalytics();

          set({ fetchMailCodeDataSuccess: true, mailCodeData: data });
        })
        .catch((error: unknown) => {
          sendMailCodeErrorAnalytics();

          const status = toString(lodashGet(error, 'response.status'));
          const isMailCodeNotFoundError = status === '404';
          const isMailCodeInvalidSourceError = status === '500';

          set({
            fetchMailCodeDataFailedMessage: lodashGet(
              i18n.t('errors.fetchMailCode.statusMessage'),
              status,
              i18n.t('errors.fetchMailCode.defaultMessage'),
            ),
            isMailCodeInvalidSourceError,
            isMailCodeNotFoundError,
          });
        });
    },
    findMailCodeDataByUniqueIdentifier: (
      uniqueIdentifier: string | undefined,
    ): MailCodeResponse | undefined => {
      const { mailCodeUniqueIdentifierData } = get();

      if (
        !uniqueIdentifier ||
        !(uniqueIdentifier in mailCodeUniqueIdentifierData)
      ) {
        return undefined;
      }

      return mailCodeUniqueIdentifierData[uniqueIdentifier];
    },
    getDeliverPinParams(
      deliverPinLocation: DeliverPinLocation,
    ): DeliverPinParams {
      const {
        computed: { isEditPhonePinPurpose },
        pinPurpose,
        primaryEmailAddress,
        primaryPhoneNumber,
      } = get();

      return {
        // When updating phone number, we should provide the email
        // as it's needed in the api to identify the existing user account.
        ...(isEditPhonePinPurpose && { email: primaryEmailAddress }),
        deliverPinLocation,
        phone: primaryPhoneNumber,
        purpose: pinPurpose,
      };
    },
    resolveMailCodeState: (): void => {
      set((state) => ({
        fetchMailCodeDataFailedMessage: null,
        fetchMailCodeDataSuccess: false,
        firstName: '',
        isMailCodeInvalidSourceError: false,
        isMailCodeNotFoundError: false,
        lastName: '',
        mailCode: null,
        mailCodeData: null,
        pinPurpose: null,
        primaryEmailAddress: '',
        primaryPhoneNumber: '',

        mailCodeUniqueIdentifierData: {
          ...state.mailCodeUniqueIdentifierData,
          [state.mailCode]: state.mailCodeData,
        },
      }));
    },
    setMailCodeActivationState: (
      params: SetMailCodeActivationStateParams,
    ): void => {
      const { determinePinPurpose } = get();
      const {
        firstNameValue,
        lastNameValue,
        primaryEmailAddressValue,
        primaryPhoneNumberValue,
        userExistsException,
      } = params;

      set({
        firstName: firstNameValue,
        lastName: lastNameValue,
        pinPurpose: determinePinPurpose(userExistsException),
        primaryEmailAddress: primaryEmailAddressValue,
        primaryPhoneNumber: primaryPhoneNumberValue,
      });
    },
  });
